import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatErr } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import LanguageToggle from "@/components/LanguageToggle";

export default function AuthPage({ mode }) {
  const { t, lang } = useLang();
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isRegister = mode === "register";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (isRegister) await register(name, email, password, lang);
      else await login(email, password);
      nav(loc.state?.from || "/dashboard");
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 grid-bg" data-testid={`auth-${mode}-page`}>
      <div className="relative hidden lg:block overflow-hidden border-r border-white/5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1464802686167-b939a6910659?auto=format&fit=crop&w=1600&q=70)",
            backgroundSize: "cover", backgroundPosition: "center", filter: "saturate(115%)",
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(3,4,11,0.5), rgba(3,4,11,0.9))" }} />
        <div className="relative z-10 p-12 flex flex-col h-full">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-logo">
            <img src="/s2s-logo-sm.png" alt="S2S — Sounds To Space"
                 className="h-36 w-auto select-none pointer-events-none"
                 style={{ filter: "drop-shadow(0 0 14px rgba(0,212,255,0.45))" }} />
          </Link>
          <div className="mt-auto">
            <div className="label-overline mb-4">Mission Briefing</div>
            <h2 className="font-display text-4xl font-black tracking-tighter leading-[0.95] max-w-md">
              {t("tagline")}
            </h2>
            <p className="text-white/70 mt-4 max-w-md leading-relaxed">{t("hero_sub")}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16 bg-[#03040B]">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-10">
            <Link to="/" className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-white/60" data-testid="auth-back">← Home</Link>
            <LanguageToggle />
          </div>

          <div className="flex justify-center mb-8" data-testid="auth-form-logo">
            <Link to="/">
              <img src="/s2s-logo-sm.png" alt="S2S — Sounds To Space"
                   className="h-36 w-auto select-none"
                   style={{ filter: "drop-shadow(0 0 18px rgba(0,212,255,0.5))" }} />
            </Link>
          </div>

          <div className="label-overline mb-2">{isRegister ? "New Operator" : "Re-Authenticate"}</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">
            {isRegister ? t("auth.signup") : t("auth.signin")}
          </h1>
          <p className="text-white/55 text-sm mt-2">
            {isRegister ? t("auth.have_account") : t("auth.need_account")}{" "}
            <Link to={isRegister ? "/login" : "/register"} data-testid="auth-switch" className="text-[#00D4FF] underline-offset-4 hover:underline">
              {isRegister ? t("auth.signin") : t("auth.signup")}
            </Link>
          </p>

          <form onSubmit={submit} className="mt-10 space-y-6">
            {isRegister && (
              <div>
                <label className="label-overline">{t("auth.name")}</label>
                <input
                  required
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mt-2 bg-transparent border-b border-[#00D4FF]/25 focus:border-[#00D4FF] outline-none py-2 text-white"
                />
              </div>
            )}
            <div>
              <label className="label-overline">{t("auth.email")}</label>
              <input
                required type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-2 bg-transparent border-b border-[#00D4FF]/25 focus:border-[#00D4FF] outline-none py-2 text-white"
              />
            </div>
            <div>
              <label className="label-overline">{t("auth.password")}</label>
              <input
                required type="password" minLength={6}
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-2 bg-transparent border-b border-[#00D4FF]/25 focus:border-[#00D4FF] outline-none py-2 text-white"
              />
            </div>

            {err && (
              <div data-testid="auth-error" className="text-[#FF3366] font-mono-data text-xs tracking-wide">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="btn-primary-s2s w-full justify-center disabled:opacity-60"
            >
              {busy ? "…" : (isRegister ? t("auth.signup") : t("auth.signin"))}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
